import type { OrderResponse } from '@palaccount/contracts';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { Amount } from '@/components/amount';
import { DocumentListPage, normalisePaged, type DocumentListConfig } from '@/components/document-list';
import { ReasonBox } from '@/components/reason-box';
import { OrderStatusChip } from '@/components/status-chip';
import { useBaseCurrency } from '@/lib/session';

import { OrderActionButtons, OrderDialogs, orderActVisibility, useOrderActions } from './order-actions';
import { orderLinks, orderProgress } from './order-lifecycle';
import { lifecycleSteps } from './order-stepper';
import { fetchOrdersPage, useOrder, useOrderStats, usePagedOrders } from './orders-api';
import { SalesTabs } from './sales-tabs';

const STATUSES: OrderResponse['status'][] = [
  'draft',
  'confirmed',
  'partially_delivered',
  'fulfilled',
  'closed',
  'cancelled',
];

type OrderStats = { openOrders: number; backlog: string; awaitingInvoice: number };

/**
 * SAL-02 — Sales Orders: the one list-and-detail sales screen, and since
 * wave 3A the FIRST CONSUMER of the shared `DocumentListPage`. Three summary
 * tiles (always shown — R1 removed the mode that hid them), the filterable
 * list, and the selected order's flow: the five-stage stepper, the
 * delivered/invoiced percentages (BR-SAL-02-03), Confirm and Cancel
 * (BR-SAL-02-07) behind sales.approve, Create invoice hidden on a cancelled
 * order (BR-SAL-02-06), and an invoice pill that OPENS the invoice
 * (BR-SAL-02-08). The stage is the server's derivation — status and stage
 * cannot disagree (BR-SAL-02-02).
 *
 * Wave 4B: every decision about the order's ACTS — the mutations, their
 * refusals, BR-MD-11-14's rate question, BR-MD-01-10's Warn-only "Continue?"
 * and Amend — lives in `order-actions.tsx`, and the chain (chips, stepper,
 * progress) in `order-lifecycle.tsx`, because the order FORM draws the same
 * acts and a second copy of that logic would drift. What stays HERE is the
 * config that names the keys, and GEN-59's cancel ask: `window.confirm` is
 * neither of §16.5.3's two patterns (BR-PERM-15), the cancel is terminal
 * and stores its reason on the record (sales-orders.ts cancel body:
 * required, 1..500; the editor's cancelled notice reads it back), so it is
 * §8's typed-reason `ReasonBox`, inside the status condition below.
 */
export function OrdersPage() {
  const { t } = useTranslation();
  /**
   * The selection lives in the URL (`?selected=`, the shared page's deep
   * link), so the acts are keyed on it — read at render, never one click
   * behind.
   */
  const [params] = useSearchParams();
  const selectedId = params.get('selected') ?? '';
  const { data: selectedOrder } = useOrder(selectedId || undefined);
  const acts = useOrderActions(selectedId, 'list');

  /** §5a's figures are the register's (GEN-107): stated in the base currency, never a literal sign. */
  const baseCurrency = useBaseCurrency();

  const config: DocumentListConfig<OrderResponse, OrderResponse['status'], OrderStats> = {
    titleKey: 'orders.title',
    tabs: <SalesTabs />,
    newAction: {
      to: '/sales/orders/new',
      /* Wave 3B: the same New label on every sales and purchasing list. */
      labelKey: 'common.new',
      permission: 'sales.create',
      reasonKey: 'perm.cannot.createSalesOrders',
    },
    statuses: STATUSES,
    statusLabel: (status) => t(`orders.status.${status}`),
    /* GEN-25: the contract takes `q` (code or customer name); the box is the shared wording. */
    search: { labelKey: 'doclist.searchLabel', placeholderKey: 'doclist.searchPlaceholder' },
    /* Wave 3B: the customer box every sales list draws; the contract already takes `partnerId`. */
    partner: { kind: 'customer' },
    useList: (filters, page, pageSize) => {
      const query = usePagedOrders(
        { status: filters.status || undefined, q: filters.q || undefined, partnerId: filters.partnerId || undefined },
        page,
        pageSize,
      );
      return { data: normalisePaged(query.data, 'orders'), isPending: query.isPending, isError: query.isError };
    },
    useById: (id) => useOrder(id ?? undefined),
    /*
     * §5a — over the WHOLE register, from SQL (PAGINATION.md §3 step 10).
     * BR-SAL-02-10 lives in the route now.
     */
    kpis: {
      useStats: () => {
        const stats = useOrderStats();
        return { data: stats.data, isPending: stats.isPending, isError: stats.isError };
      },
      tiles: [
        { key: 'open', labelKey: 'orders.tileOpen', format: 'count', value: (s) => s.openOrders },
        /* §5a: whole units — "$79,692, not $79,692.00". */
        {
          key: 'backlog',
          labelKey: 'orders.tileBacklog',
          format: 'moneyWhole',
          currency: baseCurrency,
          value: (s) => s.backlog,
        },
        { key: 'awaiting', labelKey: 'orders.tileAwaiting', format: 'count', value: (s) => s.awaitingInvoice },
      ],
    },
    card: (order) => ({
      code: order.code,
      chip: <OrderStatusChip status={order.status} />,
      title: order.partnerName,
      amount: { value: order.grandTotal, currency: order.currency },
    }),
    columns: { titleKey: 'sales.colCustomer', amountKey: 'sales.colGrandTotal', statusKey: 'sales.colStatus' },
    /* ERR-SAL-02-01 and ERR-SAL-02-02, word for word. */
    emptyKey: 'orders.noneMatch',
    selectOneKey: 'orders.selectOne',
    notice: acts.error ? (
      <p
        role="alert"
        className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
      >
        {acts.error}
      </p>
    ) : null,
    export: {
      filename: 'sales-orders',
      columns: [
        { header: t('doclist.colCode'), value: (o) => o.code },
        { header: t('doclist.colDate'), value: (o) => o.orderDate, kind: 'date' },
        { header: t('orders.fExpected'), value: (o) => o.expectedDate ?? '', kind: 'date' },
        { header: t('sales.colCustomer'), value: (o) => o.partnerName },
        { header: t('sales.colStatus'), value: (o) => t(`orders.status.${o.status}`) },
        { header: t('sales.exportColCurrency'), value: (o) => o.currency },
        { header: t('sales.colGrandTotal'), value: (o) => o.grandTotal, kind: 'number' },
      ],
      fetchPage: (filters, page, pageSize) =>
        fetchOrdersPage(
          { status: filters.status || undefined, q: filters.q || undefined, partnerId: filters.partnerId || undefined },
          page,
          pageSize,
        ),
    },
    detail: {
      /* §5c — the detail header. */
      chip: (order) => <OrderStatusChip status={order.status} />,
      /* The quotation it came from, every delivery note, the invoice
         (BR-SAL-02-08: the pill OPENS the invoice) — `order-lifecycle.tsx`. */
      links: (order) => orderLinks(order, t),
      subline: (order) => (
        <>
          {order.partnerName} · {t('orders.expected', { date: order.expectedDate ?? '—' })}
        </>
      ),
      /* §5d — the ORDER-TO-CASH stepper: green behind, blue now. D-09: a
         stage with no document yet that is the next sensible act IS the act. */
      steps: {
        labelKey: 'orders.stepper',
        of: (order) => lifecycleSteps(order, { createInvoice: acts.createInvoice, busy: acts.busy }),
      },
      /* BR-SAL-02-03 — "60% delivered · 0% invoiced". */
      progress: (order) => orderProgress(order, t),
      actions: (selected) => (
        <>
          {/* Edit, Confirm, Create delivery note, Create invoice (BR-SAL-02-06:
              hidden on a draft and on a cancelled order), Amend, Cancel
              (BR-SAL-02-07: the button exists) — `order-actions.tsx`.

              RETIRED, D59. SAL-02 §14 has a row for "Clicked Advance on a
              cancelled order" whose prescribed behaviour — the button is
              hidden — is exactly what these buttons do. Its identifier is
              NOT cited, because this build has no single Advance control:
              the stage flow is per-stage buttons, so the row names something
              that does not exist here. (rule-coverage:ignore — ERR-SAL-02-06
              is named on this line only to say it is deliberately NOT cited.) */}
          <OrderActionButtons order={selected} acts={acts} showEdit />
          {/* §8's typed-reason dialog (GEN-59). Inside the same condition as
              the button, so the dialog cannot outlive the state that allows
              the cancel. `gen72.confirmCancel` is the verb — deliberately NOT
              the trigger's exact words (reason-box.tsx's own rule). */}
          {orderActVisibility(selected).cancel ? (
            <ReasonBox
              open={acts.cancelAsk}
              title={t('orders.confirmCancelTitle')}
              prompt={t('orders.confirmCancelBody')}
              reasonLabel={t('gen72.cancelReasonLabel')}
              confirmLabel={t('gen72.confirmCancel')}
              busy={acts.busy}
              onConfirm={acts.cancel}
              onCancel={acts.closeCancel}
            />
          ) : null}
        </>
      ),
      /* §5e — the read-only order lines. */
      lines: {
        columns: [
          { key: 'item', labelKey: 'quotes.colItem' },
          { key: 'qty', labelKey: 'sales.colQty', align: 'end', className: 'w-24' },
          { key: 'unitPrice', labelKey: 'sales.colUnitPrice', align: 'end', className: 'w-32' },
          { key: 'total', labelKey: 'sales.colTotal', align: 'end', className: 'w-32' },
        ],
        rows: (order) =>
          order.lines.map((line) => ({
            key: String(line.lineNo),
            cells: [
              <span key="d" className="font-medium">{line.description}</span>,
              <bdi key="q" dir="ltr" className="font-mono">{line.qty.replace(/\.?0+$/, '')}</bdi>,
              <Amount key="u" value={line.unitPrice} currency={order.currency} />,
              <Amount key="t" value={line.lineTotal} currency={order.currency} />,
            ],
          })),
      },
      totals: (order) => ({
        labelKey: 'orders.netTaxTotal',
        amounts: [
          { value: order.subtotal, currency: order.currency },
          { value: order.taxAmount, currency: order.currency },
          { value: order.grandTotal, currency: order.currency, emphasis: true },
        ],
      }),
    },
  };

  return (
    <>
      {/* BR-MD-11-14's rate question and BR-MD-01-10's credit "Continue?",
          rendered UNCONDITIONALLY — outside the `selected` branch that draws
          the Confirm button, because both are awaited: a dialog that
          unmounted with the selection would leave the confirm hanging with no
          error anywhere. The amend reason box rides along; the cancel box is
          drawn above, inside its own condition. */}
      <OrderDialogs order={selectedOrder} acts={acts} screen="list" cancel={false} />
      <DocumentListPage config={config} />
    </>
  );
}
